import { ChangeEvent, useRef } from 'react'

import * as api from 'api/api'
import { useConfirm } from 'components/Confirm'
import { Icon } from 'components/Icon'
import { useProject } from 'components/Layout'
import { Button, Card, EmptyState, ErrorState, Spinner } from 'components/ui'
import { formatDate, formatSize } from 'lib/format'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'

/** Project document library (Figma: Admin / 03 · Site detail — Documents) — a dedicated
 *  ProjectDocument entity, not daily-report Attachments, since these files aren't tied to a specific day. */
export const DocumentsPage = () => {
  const { project, isManager } = useProject()
  const { data, error, loading, reload } = useAsync(() => api.fetchDocuments(project.id), [project.id])
  const { busy, run } = useAction()
  const confirm = useConfirm()
  const fileInput = useRef<HTMLInputElement>(null)

  const onUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (!files.length) return
    const done = await run('upload', () => api.uploadDocuments(project.id, files), t('تم رفع المستند', 'Document uploaded'))
    if (done) reload()
  }

  const onRemove = async (id: string, name: string) => {
    const sure = await confirm(t(`حذف «${name}» من مستندات المشروع؟`, `Delete "${name}" from the project documents?`), { tone: 'danger' })
    if (!sure) return
    const done = await run(id, () => api.removeDocument(id), t('تم حذف المستند', 'Document deleted'))
    if (done) reload()
  }

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('المستندات', 'Documents')}</h1>
          <p className="muted">{data ? t(`${data.length} مستند`, `${data.length} documents`) : t('عقود، تصاريح ومخططات خاصة بالمشروع', 'Contracts, permits, and drawings for this project')}</p>
        </div>
        {isManager ? (
          <>
            <input ref={fileInput} type="file" multiple hidden onChange={onUpload} />
            <Button variant="primary" icon="upload" busy={busy === 'upload'} onClick={() => fileInput.current?.click()}>
              {t('رفع مستند', 'Upload document')}
            </Button>
          </>
        ) : null}
      </div>

      {loading && !data ? (
        <Spinner />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : !data?.length ? (
        <EmptyState icon="file" title={t('لا توجد مستندات بعد', 'No documents yet')} />
      ) : (
        <Card>
          <ul className="archive">
            {data.map((document) => (
              <li key={document.id} className="archive__row">
                <button type="button" className="archive__item" onClick={() => api.downloadDocument(document.id)}>
                  <span className="archive__thumb">
                    <Icon name="file" size={20} />
                  </span>
                  <span className="archive__meta">
                    <strong>{document.originalName}</strong>
                    <small>
                      {formatSize(document.size)} · {formatDate(document.createdAt)}
                    </small>
                    <small>{document.uploadedByName}</small>
                  </span>
                  <Icon name="download" />
                </button>
                {isManager ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="trash"
                    busy={busy === document.id}
                    onClick={(event) => {
                      event.stopPropagation()
                      onRemove(document.id, document.originalName)
                    }}
                  >
                    {t('حذف', 'Delete')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}
